import {
  array,
  boolean,
  check,
  email,
  isoTimestamp,
  literal,
  minLength,
  minValue,
  nullable,
  object,
  optional,
  parse,
  picklist,
  pipe,
  regex,
  strictTuple,
  string,
} from "valibot";

import { safeIntegerSchema } from "./schema-helpers.ts";

export const auctionTextSchema = pipe(string(), minLength(1));
const cents = safeIntegerSchema(0);
const date = pipe(string(), isoTimestamp());
const status = picklist(["draft", "active", "finished"]);
const additionalChargeSchema = object({
  additionalUnitAmountCents: cents,
  category: picklist(["shipping", "handling", "other"]),
  firstUnitAmountCents: cents,
  name: auctionTextSchema,
});
const itemSchema = object({
  additionalCharge: optional(nullable(additionalChargeSchema)),
  auctionId: auctionTextSchema,
  availableQuantity: nullable(cents),
  bidCount: cents,
  bidIncrementAmount: nullable(cents),
  buyNowPriceAmount: nullable(cents),
  collectShippingAddress: boolean(),
  communityId: auctionTextSchema,
  createdAt: date,
  description: nullable(string()),
  endAt: optional(nullable(date)),
  finalizedBidCount: cents,
  finishedAt: optional(nullable(date)),
  id: auctionTextSchema,
  image: nullable(string()),
  images: array(string()),
  inventoryQuantity: nullable(cents),
  isSoldOut: boolean(),
  maxBidAmount: nullable(cents),
  maxQuantityPerOrder: optional(nullable(pipe(cents, minValue(1)))),
  minBidAmount: nullable(cents),
  name: string(),
  paidQuantity: cents,
  path: auctionTextSchema,
  position: cents,
  reservedQuantity: cents,
  saleMode: picklist(["auction", "buy_now", "hybrid"]),
  slug: auctionTextSchema,
  startAt: optional(nullable(date)),
  status,
  updatedAt: date,
});
const auctionSchema = object({
  collectShippingAddress: boolean(),
  community: optional(
    object({
      banner: optional(nullable(string())),
      color: optional(nullable(string())),
      name: optional(string()),
      thumbnail: optional(nullable(string())),
    })
  ),
  communityId: auctionTextSchema,
  configuration: object({
    collectShippingAddress: boolean(),
    endTimeEnabled: optional(nullable(boolean())),
    image: nullable(string()),
    serviceFeeDefaultChecked: boolean(),
    serviceFeeEnabled: boolean(),
    startTimeEnabled: optional(nullable(boolean())),
  }),
  createdAt: date,
  description: nullable(string()),
  endAt: date,
  id: auctionTextSchema,
  items: array(itemSchema),
  name: string(),
  path: auctionTextSchema,
  payment: optional(
    object({
      connectedAccountId: nullable(auctionTextSchema),
      currency: literal("usd"),
      publishableKey: auctionTextSchema,
    })
  ),
  position: cents,
  pricingPolicy: object({
    currency: literal("usd"),
    serviceFeeRateBasisPoints: cents,
  }),
  serviceFeeDefaultChecked: boolean(),
  serviceFeeEnabled: boolean(),
  slug: auctionTextSchema,
  startAt: date,
  status,
  updatedAt: date,
});
const pricingEntries = {
  additionalCharge: optional(nullable(additionalChargeSchema)),
  additionalChargeCents: optional(cents, 0),
  serviceFeeCents: cents,
  serviceFeeRateBasisPoints: cents,
  subtotalCents: cents,
  totalCents: cents,
};
const pricingIsConsistent = (value: {
  additionalChargeCents?: number;
  subtotalCents: number;
  serviceFeeCents: number;
  totalCents: number;
}) =>
  value.totalCents ===
  value.subtotalCents +
    value.serviceFeeCents +
    (value.additionalChargeCents ?? 0);
const bidResultEntries = {
  ...pricingEntries,
  clientSecret: nullable(auctionTextSchema),
  requiresAction: boolean(),
};
const bidResultSchema = pipe(
  object(bidResultEntries),
  check((value) => pricingIsConsistent(value)),
  check((value) => !value.requiresAction || value.clientSecret !== null)
);
const purchaseResultSchema = pipe(
  object({
    ...bidResultEntries,
    id: auctionTextSchema,
    purchaseId: auctionTextSchema,
    quantity: pipe(cents, minValue(1)),
    status: picklist([
      "pending_payment",
      "paid",
      "payment_failed",
      "expired",
      "canceled",
      "refunded",
    ]),
    totalAmount: cents,
  }),
  check((value) => pricingIsConsistent(value)),
  check(
    (value) =>
      value.id === value.purchaseId &&
      (!value.requiresAction || value.clientSecret !== null)
  )
);
const receiptSchema = pipe(
  object({
    ...pricingEntries,
    quantity: pipe(cents, minValue(1)),
    totalAmount: cents,
    unitAmount: cents,
  }),
  check((value) => pricingIsConsistent(value))
);
const submissionEntries = {
  confirmationToken: auctionTextSchema,
  email: pipe(string(), email()),
  expectedAdditionalChargeCents: optional(cents),
  expectedServiceFeeCents: optional(cents),
  expectedSubtotalCents: optional(cents),
  expectedTotalCents: optional(cents),
  includeServiceFee: optional(boolean()),
  phone: pipe(string(), regex(/^\+[1-9]\d{1,14}$/)),
};
const bidSubmissionSchema = object({
  ...submissionEntries,
  amount: pipe(cents, minValue(100)),
});
const purchaseSubmissionSchema = object({
  ...submissionEntries,
  clientRequestId: auctionTextSchema,
  paymentMethodTypes: optional(strictTuple([literal("card")])),
  quantity: pipe(cents, minValue(1)),
});
export const parseAuction = <Value>(value: Value) =>
  parse(auctionSchema, value);
export const parseAuctions = <Value>(value: Value) =>
  parse(array(auctionSchema), value);
export const parseAuctionBidResult = <Value>(value: Value) =>
  parse(bidResultSchema, value);
export const parseAuctionPurchaseResult = <Value>(value: Value) =>
  parse(purchaseResultSchema, value);
export const parseAuctionPurchaseReceipt = <Value>(value: Value) =>
  parse(receiptSchema, value);
export const parseAuctionBidSubmission = <Value>(value: Value) =>
  parse(bidSubmissionSchema, value);
export const parseAuctionPurchaseSubmission = <Value>(value: Value) =>
  parse(purchaseSubmissionSchema, value);

export const parseAuctionPurchaseStatus = <Value>(value: Value) =>
  parse(nullable(purchaseResultSchema), value);

export const parseAuctionPurchaseQuote = <Value>(value: Value) =>
  parse(
    pipe(
      object(pricingEntries),
      check((pricing) => pricingIsConsistent(pricing))
    ),
    value
  );
export const parseAuctionPurchaseQuoteSelection = <Value>(value: Value) =>
  parse(
    object({
      includeServiceFee: optional(boolean()),
      quantity: pipe(cents, minValue(1)),
    }),
    value
  );
